import { useCallback, useEffect, useId, useRef, useState, type FormEvent, type RefObject } from "react";
import { z } from "zod";
import type { AgentSettings, MachineSizeName, MachineView } from "../../shared/contracts";
import { MACHINE_SIZES } from "../../shared/contracts";
import { AgentAvatarView } from "../components/avatar";
import { LiveScreen, type ScreenHandle } from "../components/live-desktop";
import { RemoteControls } from "../components/remote-controls";
import { Badge, Button, buttonClass, Card, CommandLine, cx, ErrorNotice, Field, Input, Loading, Notice, PageHeader, RadioCard, Select, Switch, Textarea } from "../components/ui";
import { upsertMachine, useMachines, useSettings } from "../lib/agent";
import { api, errorText } from "../lib/api";
import { fmtDateTime, fmtDuration, fmtRelative, sizeLabel, sizeSpecs } from "../lib/format";
import { useNow, usePageTitle } from "../lib/hooks";
import { invalidate, mutateResource } from "../lib/store";
import { MachineStateBadge } from "./pulse";

const SIZE_NAMES = Object.keys(MACHINE_SIZES) as MachineSizeName[];

function removeMachine(id: string): void {
  mutateResource<MachineView[]>("machines", (list) => list?.filter((m) => m.id !== id));
  invalidate("dashboard");
}

function monthlyLine(name: MachineSizeName): string {
  return `≈ ${MACHINE_SIZES[name].usdPer30DaysAlwaysOn} $ / mois si toujours allumé`;
}

/**
 * /ordinateur — the cloud computer (one per user), its live screen, and the paired local computers. The screen comes
 * first and opens at once; an off computer is turned on by itself, once per visit. `live` (/ordinateur?ecran=direct,
 * the link the agent sends) also scrolls to the screen.
 */
export function ComputerPage({ live = false }: { live?: boolean }) {
  usePageTitle("Ordinateur");
  const machines = useMachines();
  const settings = useSettings();
  const agentName = settings.data?.agentName ?? "Ton agent";
  const now = useNow(30_000);
  // One automatic start per visit of the page (owned here: the screen remounting never starts the computer again).
  const startOnce = useRef(false);

  if (machines.loading) return <Loading />;
  const list = machines.data ?? [];
  const cloud = list.find((m) => m.kind === "cloud");
  const locals = list.filter((m) => m.kind === "local");

  return (
    <div>
      <PageHeader title="Son ordinateur" description={`Là où ${agentName} travaille. Tu peux le regarder, ou prendre la main.`} />
      {machines.error ? <ErrorNotice message={machines.error.message} onRetry={() => void machines.reload()} /> : null}

      <div className="flex flex-wrap items-start gap-6">
        <div className="flex min-w-0 flex-[999_1_560px] flex-col gap-6">
          {cloud?.liveView ? <CloudScreen machine={cloud} live={live} startOnce={startOnce} /> : null}
          {cloud ? null : <CreateCloudMachine />}
          {locals.length ? (
            <section aria-labelledby="my-computers">
              <h2 id="my-computers" className="mb-3 font-display text-[22px] font-medium">
                Mes ordinateurs
              </h2>
              <ul className="grid gap-3 lg:grid-cols-2">
                {locals.map((m) => (
                  <LocalMachineCard key={m.id} machine={m} now={now} />
                ))}
              </ul>
            </section>
          ) : null}
        </div>

        <aside className="flex min-w-0 flex-[1_1_300px] flex-col gap-4">
          {cloud ? <CloudMachineCard machine={cloud} now={now} /> : null}
          <PairingInstructions agentName={agentName} />
        </aside>
      </div>
    </div>
  );
}

function CreateCloudMachine() {
  const [size, setSize] = useState<MachineSizeName>("standard-2");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function create(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      const machine = await api("POST /api/machines/cloud", { body: { size } });
      upsertMachine(machine);
    } catch (err) {
      setError(errorText(err));
      invalidate("machines");
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="p-5 sm:p-6">
      <form onSubmit={create} noValidate>
        <fieldset>
          <legend className="font-display text-[21px] font-medium">Choisis sa puissance</legend>
          <p className="mt-1 text-[13.5px] text-muted">Tu ne paies que lorsqu'il est allumé. Il s'éteint tout seul.</p>
          <div className="mt-3 grid gap-2 sm:grid-cols-2">
            {SIZE_NAMES.map((name) => {
              const s = MACHINE_SIZES[name];
              return (
                <RadioCard key={name} name="machine-size" value={name} label={s.label} checked={size === name} onChange={() => setSize(name)}>
                  <span className="block">{sizeSpecs(name)}</span>
                  <span className="block">{monthlyLine(name)}</span>
                </RadioCard>
              );
            })}
          </div>
        </fieldset>
        {error ? (
          <Notice tone="danger" role="alert" className="mt-4">
            {error}
          </Notice>
        ) : null}
        <Button type="submit" className="mt-4" busy={busy}>
          Créer mon ordinateur
        </Button>
      </form>
    </Card>
  );
}

/**
 * The cloud computer's screen on a dark stage: the desktop in a large rounded card on the accent gradient, scaled to
 * fit, and under it who has the controls. View-only while the agent has them; « Prendre les commandes » hands the
 * owner mouse and keyboard (and the phone controls) and tells the agent to wait; « Rendre la main » gives them back.
 */
export function CloudScreen({
  machine,
  live = false,
  startOnce,
  agent: agentOverride,
  onMachine,
  autoConnect = true,
}: {
  machine: MachineView;
  live?: boolean;
  /** /ordinateur: an off computer starts by itself once per visit (absent: it starts only on demand). */
  startOnce?: RefObject<boolean>;
  /** The agent working on this computer (an extra agent's page, « Mes agents »); default: the main agent. */
  agent?: { name: string; avatar: AgentSettings["avatar"]; accentColor?: AgentSettings["accentColor"]; character?: AgentSettings["character"] };
  /** Where an updated machine goes (default: the machines list). */
  onMachine?: (machine: MachineView) => void;
  autoConnect?: boolean;
}) {
  const settings = useSettings();
  const stageRef = useRef<HTMLElement>(null);
  const [screen, setScreen] = useState<ScreenHandle | null>(null);
  const [clipboard, setClipboard] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const name = agentOverride?.name ?? settings.data?.agentName ?? "Ton agent";
  const agent = agentOverride ?? {
    name,
    avatar: settings.data?.avatar ?? null,
    character: settings.data?.character ?? null,
    accentColor: settings.data?.accentColor ?? null,
  };
  const running = machine.state === "running";
  const control = running && machine.userControl === true;

  useEffect(() => {
    if (live) stageRef.current?.scrollIntoView({ block: "start" });
  }, [live]);

  const onClipboard = useCallback((text: string) => setClipboard(text), []);

  async function setControl(take: boolean) {
    if (busy) return;
    setBusy(true);
    setError(null);
    try {
      (onMachine ?? upsertMachine)(await api("POST /api/machines/:id/control", { params: { id: machine.id }, body: { take } }));
    } catch (err) {
      setError(errorText(err));
      if (!onMachine) invalidate("machines");
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      ref={stageRef}
      id="ecran-direct"
      aria-label="Écran de l'ordinateur cloud"
      className="scroll-mt-4"
    >
      <div className="rounded-[22px] bg-[#23211c] p-2.5 text-white max-sm:-mx-4 max-sm:rounded-none">
      <p className="flex items-center gap-2 px-2 pt-1 pb-2.5 text-[12.5px] text-[#b5ad9f]">
        <span aria-hidden="true" className={cx("h-[7px] w-[7px] rounded-full", running ? "animate-pulse bg-[#d98a63]" : machine.state === "sleeping" ? "bg-[#b5ad9f]" : "bg-[#6b655b]")} />
        <span className="font-semibold text-[#f2ede4]">{running ? "En direct" : machine.state === "sleeping" ? "En veille" : "Éteint"}</span>
      </p>
      <LiveScreen
        machine={machine}
        autoConnect={autoConnect}
        startOnce={startOnce}
        stage
        viewOnly={!control}
        onReady={setScreen}
        onClipboard={onClipboard}
        onMachine={onMachine}
        agent={agent}
      />
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-3" data-testid="control-bar">
        <Button busy={busy} disabled={!running} onClick={() => void setControl(!control)}>
          {control ? "Rendre la main" : "Prendre la main"}
        </Button>
        <p className="flex min-w-0 items-center gap-2.5 text-[14.5px] text-muted" role="status">
          <AgentAvatarView agent={agent} size={30} status={machine.agentActive && !control ? "computer" : undefined} />
          <span className="min-w-0">
            {control ? (
              <>
                <strong className="font-semibold text-fg">Tu as les commandes</strong>
                {machine.agentActive ? ` · ${name} attend que tu lui rendes la main` : ""}
              </>
            ) : (
              <>
                <strong className="font-semibold text-fg">{name}</strong> {machine.agentActive ? "est aux commandes" : "est disponible"}
              </>
            )}
          </span>
        </p>
      </div>
      {error ? (
        <p role="alert" className="mt-3 text-[13px] text-danger">
          {error}
        </p>
      ) : null}
      {control ? <RemoteControls screen={screen} remoteClipboard={clipboard} className="mt-4" /> : null}
    </section>
  );
}

function CloudMachineCard({ machine, now }: { machine: MachineView; now: number }) {
  const [busy, setBusy] = useState<"start" | "stop" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const sizeName = typeof machine.size === "string" ? machine.size : null;
  const on = machine.state === "running" || machine.state === "starting" || machine.state === "sleeping";

  async function power(action: "start" | "stop") {
    if (busy) return;
    setBusy(action);
    setError(null);
    try {
      const updated =
        action === "start"
          ? await api("POST /api/machines/:id/start", { params: { id: machine.id }, body: {} })
          : await api("POST /api/machines/:id/stop", { params: { id: machine.id }, body: {} });
      upsertMachine(updated);
    } catch (err) {
      setError(errorText(err));
      invalidate("machines");
    } finally {
      setBusy(null);
    }
  }

  return (
    <div>
      <Card data-testid="machine-card" className="p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <h2 className="font-display text-[21px] leading-snug font-medium wrap-anywhere">{machine.name}</h2>
            <p className="mt-0.5 text-[12.5px] text-muted">
              {sizeLabel(machine.size)} · {sizeSpecs(machine.size)}
            </p>
            {sizeName ? <p className="text-[12.5px] text-subtle">{monthlyLine(sizeName)}</p> : null}
          </div>
          <MachineStateBadge state={machine.state} />
        </div>
        {machine.lastSeenAt ? <p className="mt-2 text-[12px] text-subtle">Dernière activité {fmtRelative(machine.lastSeenAt, now)}</p> : null}
        {machine.sleep ? <SleepLine sleep={machine.sleep} now={now} /> : null}
        <div className="mt-4 flex flex-wrap gap-2">
          {on || machine.state === "stopping" ? (
            <Button variant="secondary" busy={busy === "stop"} disabled={busy !== null || machine.state === "stopping"} onClick={() => void power("stop")}>
              Arrêter
            </Button>
          ) : (
            <Button busy={busy === "start"} disabled={busy !== null} onClick={() => void power("start")}>
              Démarrer
            </Button>
          )}
        </div>
        {machine.state === "unavailable" ? (
          <Notice tone="warning" className="mt-3">
            L'ordinateur est momentanément indisponible. Réessaie de le démarrer dans quelques minutes.
          </Notice>
        ) : null}
        {error ? (
          <Notice tone="danger" role="alert" className="mt-3">
            {error}
          </Notice>
        ) : null}
        <IdleStopForm machine={machine} />
        <SleepStopForm machine={machine} />
        {sizeName ? <PowerForm machine={machine} /> : null}
      </Card>
    </div>
  );
}

/**
 * « Puissance » (auto-scaling, shared/autoscale.ts): the owner's size (the floor), the maximum size the computer may
 * grow to for tasks run in parallel, or a fixed size; and the recent changes with their cost.
 */
function PowerForm({ machine }: { machine: MachineView }) {
  const auto = machine.autoscale;
  const base = auto?.baseSize ?? (typeof machine.size === "string" ? machine.size : "standard-1");
  const [baseSize, setBaseSize] = useState<MachineSizeName>(base);
  const [maxSize, setMaxSize] = useState<MachineSizeName>(auto?.maxSize ?? "standard-3");
  const [pinned, setPinned] = useState(auto?.pinned ?? false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const baseId = useId();
  const maxId = useId();
  const pinId = useId();

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (!pinned && SIZE_NAMES.indexOf(maxSize) < SIZE_NAMES.indexOf(baseSize)) {
      setError("La taille maximale ne peut pas être plus petite que la taille de base.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      let updated = await api("PATCH /api/machines/:id", { params: { id: machine.id }, body: { autoscale: { maxSize, pinned } } });
      if (baseSize !== base) updated = await api("PATCH /api/machines/:id", { params: { id: machine.id }, body: { size: baseSize } });
      upsertMachine(updated);
      setSaved(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const current = typeof machine.size === "string" ? machine.size : base;
  const events = auto?.events ?? [];
  return (
    <form onSubmit={save} noValidate className="mt-5 border-t border-sunken pt-4" data-testid="power-form">
      <h3 className="font-display text-[19px] leading-snug font-medium">Puissance</h3>
      <p className="mt-0.5 text-[13px] text-muted">Il grandit seul pour les tâches en parallèle, puis revient à sa taille de base.</p>
      <div className="mt-3 flex flex-wrap items-end gap-3">
        <Field label="Taille de base" htmlFor={baseId} className="min-w-0 flex-[1_1_180px]">
          <Select id={baseId} value={baseSize} onChange={(e) => setBaseSize(e.target.value as MachineSizeName)}>
            {SIZE_NAMES.map((n) => (
              <option key={n} value={n}>
                {MACHINE_SIZES[n].label} · {MACHINE_SIZES[n].memoryGiB} Go
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Taille maximale" htmlFor={maxId} className="min-w-0 flex-[1_1_180px]" hint={pinned ? "Sans effet : la taille est fixe." : undefined}>
          <Select id={maxId} value={maxSize} disabled={pinned} onChange={(e) => setMaxSize(e.target.value as MachineSizeName)}>
            {SIZE_NAMES.map((n) => (
              <option key={n} value={n}>
                {MACHINE_SIZES[n].label} · ≈ {(MACHINE_SIZES[n].usdPer30DaysAlwaysOn / 720).toFixed(2).replace(".", ",")} $/h
              </option>
            ))}
          </Select>
        </Field>
      </div>
      <Switch id={pinId} label="Taille fixe" description="Toujours la taille de base, même pour des tâches en parallèle." checked={pinned} onChange={setPinned} />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <Button type="submit" variant="secondary" busy={busy}>
          Enregistrer
        </Button>
        {saved ? (
          <span className="text-[12.5px] text-success" role="status">
            C'est noté.
          </span>
        ) : null}
      </div>
      {error ? (
        <Notice tone="danger" role="alert" className="mt-3">
          {error}
        </Notice>
      ) : null}
      <p className="mt-3 text-[12.5px] text-muted" data-testid="power-current">
        En ce moment : {MACHINE_SIZES[current as MachineSizeName]?.label ?? sizeLabel(machine.size)}
        {auto?.pending ? ` · passage en ${MACHINE_SIZES[auto.pending.to].label} dès que les tâches en cours se terminent` : ""}
        {auto?.queued ? ` · ${auto.queued} tâche${auto.queued > 1 ? "s" : ""} en attente d'un bureau` : ""}
      </p>
      {events.length ? (
        <ul className="mt-2 divide-y divide-line rounded-[14px] border border-line bg-elev text-[12.5px]" data-testid="power-events">
          {events.slice(0, 5).map((ev) => (
            <li key={`${ev.at}-${ev.to}`} className="flex flex-wrap items-baseline gap-x-2 px-3 py-2">
              <span className="text-subtle tabular-nums">{fmtDateTime(ev.at)}</span>
              <span className="min-w-0 flex-1 text-fg">
                {ev.note}
                {ev.resizeMs ? <span className="text-muted">{` (redémarré en ${Math.round(ev.resizeMs / 1000)} s)`}</span> : null}
              </span>
            </li>
          ))}
        </ul>
      ) : null}
    </form>
  );
}

/** Why a sleeping computer stops for good when it does (MachineView.sleep.why). */
const SLEEP_WHY: Record<NonNullable<NonNullable<MachineView["sleep"]>["why"]>, string> = {
  setting: "selon ton réglage",
  active_hours: "à la fin de tes heures actives",
  quiet: "après 2 h sans activité",
};

/** « En veille depuis 12 min · ≈ 0,04 $/h · arrêt complet vers 23:10 » (the sleep's estimated cost). */
function SleepLine({ sleep, now }: { sleep: NonNullable<MachineView["sleep"]>; now: number }) {
  const hours = Math.max(0, now - sleep.since) / 3_600_000;
  const spent = hours * sleep.usdPerHour;
  return (
    <p className="mt-1 text-[12.5px] text-muted" data-testid="sleep-cost">
      En veille depuis {fmtDuration(Math.max(60_000, now - sleep.since))} · ≈ {sleep.usdPerHour.toFixed(3).replace(".", ",")} $/h
      {spent >= 0.005 ? ` (≈ ${spent.toFixed(2).replace(".", ",")} $ jusqu'ici)` : ""}
      {sleep.stopAt ? ` · arrêt complet vers ${fmtDateTime(sleep.stopAt)}${sleep.why ? `, ${SLEEP_WHY[sleep.why]}` : ""}` : ""}
    </p>
  );
}

const SLEEP_CHOICES: { value: string; label: string }[] = [
  { value: "auto", label: "Selon mon rythme (recommandé)" },
  { value: "30", label: "Après 30 min de veille" },
  { value: "60", label: "Après 1 h de veille" },
  { value: "120", label: "Après 2 h de veille" },
  { value: "240", label: "Après 4 h de veille" },
  { value: "0", label: "Pas de veille : arrêt direct" },
];

/**
 * « Arrêt complet » after the light sleep: by the owner's rhythm (asleep during his active hours, stopped at night or
 * after 2 h without activity), after a fixed time, or no sleep at all (the old direct stop).
 */
function SleepStopForm({ machine }: { machine: MachineView }) {
  const id = useId();
  const current = machine.sleepStop === undefined ? "auto" : String(machine.sleepStop);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function change(value: string) {
    setBusy(true);
    setError(null);
    try {
      upsertMachine(await api("PATCH /api/machines/:id", { params: { id: machine.id }, body: { sleepStop: value === "auto" ? "auto" : Number(value) } }));
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const choices = SLEEP_CHOICES.some((c) => c.value === current) ? SLEEP_CHOICES : [...SLEEP_CHOICES, { value: current, label: `Après ${current} min de veille` }];
  return (
    <div className="mt-4" data-testid="sleep-stop">
      <Field label="Arrêt complet après la veille" htmlFor={id} error={error} hint="En veille, il se réveille en une seconde ; éteint, il redémarre en une minute environ.">
        <Select id={id} value={current} disabled={busy} onChange={(e) => void change(e.target.value)}>
          {choices.map((c) => (
            <option key={c.value} value={c.value}>
              {c.label}
            </option>
          ))}
        </Select>
      </Field>
    </div>
  );
}

const idleSchema = z.coerce.number().int().min(0).max(1440);

function IdleStopForm({ machine }: { machine: MachineView }) {
  const id = useId();
  const current = machine.idleStopMinutes ?? 15;
  const [value, setValue] = useState(String(current));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  async function save(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = idleSchema.safeParse(value.trim());
    if (!parsed.success) {
      setError("Indique un nombre de minutes entre 0 et 1 440.");
      return;
    }
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const updated = await api("PATCH /api/machines/:id", { params: { id: machine.id }, body: { idleStopMinutes: parsed.data } });
      upsertMachine(updated);
      setSaved(true);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  }

  const minutes = Number(value);
  return (
    <form onSubmit={save} noValidate className="mt-5 border-t border-sunken pt-4">
      <div className="flex flex-wrap items-end gap-3">
        <Field
          label="Mise en veille (minutes d'inactivité)"
          htmlFor={id}
          className="min-w-0 flex-[1_1_180px]"
          error={error}
          hint={Number.isFinite(minutes) && minutes > 0 ? `Se met en veille après ${minutes} min d'inactivité` : "0 = toujours allumé"}
        >
          <Input
            id={id}
            type="number"
            inputMode="numeric"
            min={0}
            max={1440}
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
              setSaved(false);
            }}
          />
        </Field>
        <Button type="submit" variant="secondary" busy={busy}>
          Enregistrer
        </Button>
        {saved ? (
          <span className="pb-2.5 text-[12.5px] text-success" role="status">
            C'est noté.
          </span>
        ) : null}
      </div>
      {current === 0 ? <p className="mt-2 text-[12.5px] text-muted">Toujours allumé : facturé en continu.</p> : null}
    </form>
  );
}

const rootsSchema = z.array(z.string().trim().min(1).max(500)).min(1).max(20);

function LocalMachineCard({ machine, now }: { machine: MachineView; now: number }) {
  const policy = machine.policy ?? { allowedRoots: [], unattendedGui: false, shell: false };
  const [editing, setEditing] = useState(false);
  const [roots, setRoots] = useState(policy.allowedRoots.join("\n"));
  const [gui, setGui] = useState(policy.unattendedGui);
  const [shell, setShell] = useState(policy.shell);
  const [busy, setBusy] = useState<"save" | "revoke" | null>(null);
  const [confirmRevoke, setConfirmRevoke] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const rootsId = useId();
  const baseId = useId();

  async function savePolicy(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const parsed = rootsSchema.safeParse(roots.split("\n").map((r) => r.trim()).filter(Boolean));
    if (!parsed.success) {
      setError("Indique au moins un dossier (un chemin par ligne, 20 maximum).");
      return;
    }
    setBusy("save");
    setError(null);
    try {
      const updated = await api("PATCH /api/machines/:id", {
        params: { id: machine.id },
        body: { policy: { allowedRoots: parsed.data, unattendedGui: gui, shell } },
      });
      upsertMachine(updated);
      setEditing(false);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(null);
    }
  }

  async function revoke() {
    setBusy("revoke");
    setError(null);
    try {
      await api("DELETE /api/machines/:id", { params: { id: machine.id } });
      removeMachine(machine.id);
    } catch (err) {
      setError(errorText(err));
      setBusy(null);
    }
  }

  return (
    <li data-testid="machine-card" className="min-w-0 rounded-[22px] border border-line bg-elev p-5">
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <h3 className="font-display text-[20px] leading-snug font-medium wrap-anywhere">{machine.name}</h3>
          <p className="text-[12.5px] text-muted">
            {[machine.os, machine.arch].filter(Boolean).join(" · ") || "Ordinateur local"}
            {machine.lastSeenAt && machine.state !== "online" ? ` · vu ${fmtRelative(machine.lastSeenAt, now)}` : ""}
          </p>
        </div>
        <MachineStateBadge state={machine.state} />
      </div>

      {!editing ? (
        <div className="mt-3 space-y-1.5 text-[13px]">
          <p className="text-muted">Dossiers autorisés :</p>
          <ul className="space-y-0.5">
            {policy.allowedRoots.length ? (
              policy.allowedRoots.map((r) => (
                <li key={r} className="font-mono text-[12.5px] wrap-anywhere">
                  {r}
                </li>
              ))
            ) : (
              <li className="text-subtle">Aucun</li>
            )}
          </ul>
          <div className="flex flex-wrap gap-1.5 pt-1">
            <Badge tone={policy.unattendedGui ? "warning" : "neutral"}>{policy.unattendedGui ? "Écran sans toi : oui" : "Écran sans toi : non"}</Badge>
            <Badge tone={policy.shell ? "warning" : "neutral"}>{policy.shell ? "Terminal : oui" : "Terminal : non"}</Badge>
          </div>
          <p className="pt-1 text-[12px] text-subtle">Relié {fmtDateTime(machine.createdAt, now)}</p>
        </div>
      ) : (
        <form onSubmit={savePolicy} noValidate className="mt-3 space-y-3">
          <Field label="Dossiers autorisés" htmlFor={rootsId} hint="Un chemin par ligne.">
            <Textarea id={rootsId} value={roots} className="min-h-20 font-mono text-[13px]" onChange={(e) => setRoots(e.target.value)} />
          </Field>
          <Switch id={`${baseId}-gui`} label="Contrôle de l'écran sans moi" description="Il peut utiliser la souris et le clavier quand tu n'es pas devant." checked={gui} onChange={setGui} />
          <Switch id={`${baseId}-shell`} label="Terminal" description="Il peut lancer des commandes (dans les dossiers autorisés)." checked={shell} onChange={setShell} />
          <div className="flex flex-wrap gap-2">
            <Button type="submit" size="sm" busy={busy === "save"}>
              Enregistrer
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
              Annuler
            </Button>
          </div>
        </form>
      )}

      {error ? (
        <Notice tone="danger" role="alert" className="mt-3">
          {error}
        </Notice>
      ) : null}

      {!editing ? (
        <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-line pt-3">
          <Button
            size="sm"
            variant="secondary"
            onClick={() => {
              setRoots(policy.allowedRoots.join("\n"));
              setGui(policy.unattendedGui);
              setShell(policy.shell);
              setEditing(true);
            }}
          >
            Modifier les autorisations
          </Button>
          {confirmRevoke ? (
            <>
              <Button size="sm" variant="danger" busy={busy === "revoke"} onClick={() => void revoke()}>
                Confirmer la révocation
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setConfirmRevoke(false)}>
                Annuler
              </Button>
            </>
          ) : (
            <Button size="sm" variant="ghost" onClick={() => setConfirmRevoke(true)}>
              Révoquer
            </Button>
          )}
        </div>
      ) : null}
    </li>
  );
}

function PairingInstructions({ agentName }: { agentName: string }) {
  const origin = location.origin;
  return (
    <section aria-labelledby="pair-title" className="flex flex-col gap-2.5 rounded-[22px] bg-sunken p-5">
      <h2 id="pair-title" className="font-display text-[21px] font-medium">
        Relier ton ordinateur
      </h2>
      <p className="text-[14px] leading-normal text-body">
        {agentName} peut aussi travailler sur ton ordinateur, dans les dossiers que tu choisis. Le terminal reste fermé par défaut.
      </p>
      <details className="group">
        <summary className={cx(buttonClass("primary", "md"), "w-full cursor-pointer list-none [&::-webkit-details-marker]:hidden")}>Voir les commandes</summary>
        <div className="mt-3 space-y-2">
          <p className="text-[12.5px] text-muted">Dans un terminal (Node.js 22 ou plus), macOS et Linux :</p>
          <CommandLine command={`curl -fsSL ${origin}/pulsez-computer.mjs -o pulsez-computer.mjs`} />
          <p className="text-[12.5px] text-muted">Windows (PowerShell) :</p>
          <CommandLine command={`iwr ${origin}/pulsez-computer.mjs -OutFile pulsez-computer.mjs`} />
          <p className="text-[12.5px] text-muted">Puis, partout, et ouvre le lien affiché :</p>
          <CommandLine command={`node pulsez-computer.mjs pair --server ${origin} --run`} />
        </div>
      </details>
    </section>
  );
}
